/*
* Created on Wed Oct 11 2023 4:46:23 PM
*
* ***************************************************************************************
* Copyright (c) AVPS Technologies Inc 2023 
*
* All Rights Reserved
* Unpublished rights are reserved under the copyright laws of the United States.
* The software contained in this media is proprietary to and embodies confidential
* the technology of AVPS Technologies Inc. Possession, use, duplication or
* dissemination of the software and media is authorized only pursuant to a valid
* written license from AVPS Technologies Inc.
*
* The above copyright notice and this permission notice shall be included in
* all copies or substantial portions of the Software.
*
* RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
* is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
* 252.227-7013 or in FAR 52.227-19, as applicable.
****************************************************************************************
*/
import { motion } from 'framer-motion';
import { Calendar, Search, SlidersHorizontal, Sparkles } from 'lucide-react';
import ProductResultCard from '../components/ProductResultCard';

const SearchResults = ({
  results,
  searchCriteria,
  onSelectProduct,
  onBackToSearch,
  onModifySearch,
  hasDiscounts,
  onShowDiscounts,
  onUpdatePeople,
  companyTimezone
}) => {

  /**
   * Build one card entry per matchingProductGroup.
   *
   * matchingProductGroups structure:
   * [{
   *   price: number,
   *   durationType: string | null,
   *   productIds: number[],
   *   slots: [{
   *     date, startTime, endTime, status ("OPEN" | "RESERVED"),
   *     assignedProductId, suggestedProduct
   *   }]
   * }]
   *
   * Strategy:
   *  - One card per group (not per suggestedProduct inside each slot)
   *  - Use the first slot's suggestedProduct for display info (name, image, etc.)
   *  - Accumulate all slots from the group, using group-level slot status (OPEN/RESERVED)
   *  - Override price with group-level price
   *
   * The suggestedProduct inside each slot changes per slot (it's whichever desk
   * the system recommends for that hour). Deduplicating by suggestedProduct.primaryId
   * would collapse a multi-product group into a single card — which is wrong.
   * One group = one bookable option = one card.
   */
  const buildProductsFromGroups = () => {
    const groups = results?.matchingProductGroups;
    if (!groups || !Array.isArray(groups) || groups.length === 0) return [];

    const productList = [];

    groups.forEach((group, groupIndex) => {
      if (!group.slots || !Array.isArray(group.slots) || group.slots.length === 0) return;

      // Use the first available suggestedProduct for display info
      const representativeProduct =
        group.slots.find((s) => s.suggestedProduct)?.suggestedProduct ?? null;
      if (!representativeProduct) return;

      // Build slots array from group-level slot data (authoritative status)
      const slots = group.slots.map((slotEntry) => ({
        durationType: slotEntry.durationType ?? null,
        date: slotEntry.date,
        eligibleProducts: null,
        // ✅ Group-level status is authoritative (OPEN / RESERVED)
        status: slotEntry.status,
        startTime: slotEntry.startTime,
        endTime: slotEntry.endTime,
        intervalInMinutes:
          slotEntry.intervalInMinutes ??
          representativeProduct.slots?.[0]?.intervalInMinutes ??
          60
      }));

      // One card per group — use a stable unique key combining group index + productIds
      const groupKey = `group-${groupIndex}-${(group.productIds || []).join('-')}`;

      productList.push({
        ...representativeProduct,
        // Stable unique id for React key and dedup — not a real productId
        _groupKey: groupKey,
        // Override price with group-level canonical price
        price: {
          ...(representativeProduct.price || {}),
          priceAmt: group.price ?? representativeProduct.price?.priceAmt ?? 0
        },
        // Expose how many products are in this group (for display if needed)
        _groupProductCount: (group.productIds || []).length,
        slots
      });
    });

    return productList;
  };

  const activeProducts = buildProductsFromGroups().filter(
    (product) =>
      product.status === 'ACTIVE' &&
      (!product.capacity || searchCriteria.people <= product.capacity)
  );

  const handleNewOrModifySearch = () => {
    onModifySearch();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="space-y-4 sm:space-y-6 lg:space-y-8 w-full"
    >
      {/* Results Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 sm:gap-5 lg:gap-6">

          {/* Top row: icon + title + badge */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                <Search className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 truncate">
                  Available Spaces
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Perfect matches for your needs
                </p>
              </div>
            </div>

            {/* Action buttons — right-aligned on sm+ */}
            <div className="flex flex-row flex-wrap gap-2 sm:gap-3 shrink-0">
              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleNewOrModifySearch}
                className="flex items-center justify-center gap-2 border-2 border-slate-300 text-slate-700 px-4 py-2.5 sm:px-5 sm:py-3 text-sm font-semibold hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 rounded-xl shadow-sm hover:shadow-md"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Modify Search</span>
              </motion.button>

              {hasDiscounts && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onShowDiscounts}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 sm:px-5 sm:py-3 text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>View Offers</span>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-200" />
                </motion.button>
              )}
            </div>
          </div>

          {/* Meta chips row */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 sm:py-2 bg-slate-50 rounded-lg border border-slate-200">
              <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-slate-700 whitespace-nowrap">
                {searchCriteria.date}
              </span>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 sm:py-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-xs sm:text-sm font-bold text-blue-700 whitespace-nowrap">
                {activeProducts.length}{' '}
                {activeProducts.length === 1 ? 'space' : 'spaces'} found
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Results List */}
      {activeProducts.length > 0 && (
        <motion.div
          className="space-y-4 sm:space-y-5"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
        >
          {activeProducts.map((product) => (
            <motion.div
              key={product._groupKey || product.primaryId}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <ProductResultCard
                product={product}
                searchCriteria={searchCriteria}
                onSelect={onSelectProduct}
                onUpdatePeople={onUpdatePeople}
                onModifySearch={onModifySearch}
                hasDiscounts={hasDiscounts}
                companyTimezone={companyTimezone}
              />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Empty State */}
      {activeProducts.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-2xl border border-slate-200"
        >
          <div className="max-w-md mx-auto px-5 sm:px-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-5 sm:mb-6 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shadow-inner">
              <Search className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 mb-3">
              No Available Spaces
            </h3>
            <p className="text-sm sm:text-base text-slate-600 mb-7 sm:mb-8 leading-relaxed">
              We couldn't find any spaces matching your criteria. Try adjusting your
              search parameters to see more options.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNewOrModifySearch}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 text-sm sm:text-base font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 rounded-xl shadow-lg hover:shadow-xl"
              >
                <SlidersHorizontal className="w-4 h-4 sm:w-5 sm:h-5" />
                Modify Your Search
              </motion.button>
            </div>

            <div className="mt-7 sm:mt-8 pt-7 sm:pt-8 border-t border-slate-200">
              <p className="text-xs sm:text-sm text-slate-500 mb-3 font-semibold">
                Suggestions:
              </p>
              <ul className="text-xs sm:text-sm text-slate-600 space-y-2">
                <li className="flex items-center justify-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0" />
                  Try selecting a different date
                </li>
                <li className="flex items-center justify-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0" />
                  Adjust the number of people
                </li>
                <li className="flex items-center justify-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0" />
                  Check different time slots
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SearchResults;
